import { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { FormSpy, useFormState } from 'react-final-form';
import { getIn } from 'final-form';
import { v4 as uuidv4 } from 'uuid';
import { isNil } from 'ramda';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmIsNeedActSaveOrSign } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmErknmViolationEvidence } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmViolationEvidence';
import { Row } from 'UIPackage/FormModule/components/Row';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { checkHasErrorInCollapseBlock } from 'functions/checkHasErrorInCollapseBlock';
import { TextInputEditableLinkField } from 'UIPackage/MainModule/components/TextInputEditableLinkField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { createPassportEventSelectOptions } from 'KnmErknmPackage/ErknmViolationsModule/common/createPassportEventSelectOptions';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import {
  isPhotoTypeDocConfirmationViolation,
  isVideoTypeDocConfirmationViolation,
} from 'KnmErknmPackage/MainModule/common/isTypeDocConfirmationViolationCode';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { ErknmActViolationEventAttachmentsField } from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationEventAttachmentsField';
import { removeMaterialsLinkedToVideoFixations } from 'KnmErknmPackage/MainModule/common/cleanupLinkedViolationFormData';
import { ICommonFormRef } from 'KnmErknmPackage/MainModule/interfaces/ICommonFormRef';

import { Button } from '../../../../UIPackage/MainModule/components/Button';
import { getUserRoleIsSupervisor } from '../../../../AuthPackage/MainModule/selectors/authSelectors';
import styles from './ErknmEvidence.module.scss';

interface IErknmEvidenceProps {
  id?: string;
  name: string;
  watchOnly: boolean;
  formRef?: IKnmFormRef | IDecisionFormRef;
  isDecision?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  onlyLinkView?: boolean;
  disableOriginPassportLinkEditable?: boolean;
  updateAction?: () => void;
  maxDate?: Date;
  minDate?: Date;
  passportEvents?: IKnmErknmFormEvent[];
}

interface IErknmEvidenceStoreProps {
  isSupervisor: boolean;
  isNeedActSaveOrSign?: boolean;
}

const ErknmEvidenceWithoutStore = ({
  id,
  isSupervisor,
  formRef,
  name,
  watchOnly,
  isDecision,
  isKnm,
  isPm,
  isNeedActSaveOrSign = false,
  onlyLinkView,
  disableOriginPassportLinkEditable,
  maxDate,
  minDate,
  updateAction,
  passportEvents,
}: IErknmEvidenceProps & IErknmEvidenceStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const fieldName = name;
  const { values } = useFormState({ subscription: { values: true } });

  const eventsFromForm = useMemo(() => {
    if (passportEvents?.length) {
      return passportEvents;
    }

    return (getIn(values, 'events') as IKnmErknmFormEvent[] | undefined) || [];
  }, [passportEvents, values]);

  const eventOptions = useMemo(() => createPassportEventSelectOptions(eventsFromForm), [eventsFromForm]);

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addEvidence = useCallback(() => {
    if (!watchOnly && push) {
      const addingEvidence: IKnmErknmViolationEvidence = {
        guid: uuidv4(),
        eventOption: isDecision && eventOptions.length ? eventOptions[0] || null : null,
        typeDocConfirmationViolation: null,
        date: undefined,
        description: undefined,
        link: undefined,
        attachments: null,
        listId: uuidv4(),
        isNew: true,
      };

      push(fieldName, addingEvidence);
    }
  }, [eventOptions, fieldName, isDecision, push, watchOnly]);

  const makeRemoveEvidenceHandler = useCallback(
    (index: number) => () => {
      if (watchOnly || !remove) {
        return;
      }

      const evidences = (formRef as ICommonFormRef | undefined)?.current.getFieldState(fieldName)?.value as
        | IKnmErknmViolationEvidence[]
        | undefined;
      const removedGuid = evidences?.[index]?.guid;

      remove(fieldName, index);

      // Удаляем связанные подтверждающие материалы из всех нарушений акта.
      if (removedGuid) {
        removeMaterialsLinkedToVideoFixations(formRef as ICommonFormRef | undefined, fieldName, [removedGuid]);
      }
    },
    [fieldName, formRef, remove, watchOnly],
  );

  const handleEventChange = useCallback(
    (evidenceFieldName: string) => {
      // @ts-ignore
      formRef?.current.change(`${evidenceFieldName}.typeDocConfirmationViolation`);
      // @ts-ignore
      formRef?.current.change(`${evidenceFieldName}.description`);
      // @ts-ignore
      formRef?.current.change(`${evidenceFieldName}.link`);
      // @ts-ignore
      formRef?.current.change(`${evidenceFieldName}.attachments`);
      // @ts-ignore
      formRef?.current.change(`${evidenceFieldName}.date`);
    },
    [formRef],
  );

  const handleTypeChange = useCallback(
    (evidenceFieldName: string) => {
      // @ts-ignore
      formRef?.current.change(`${evidenceFieldName}.link`);
      // @ts-ignore
      formRef?.current.change(`${evidenceFieldName}.attachments`);
    },
    [formRef],
  );

  return (
    <FieldBlock
      id={id}
      label={getTitleFieldWithRedStar({
        label: 'Ссылка на видеозапись или фотоматериалы',
        field: fieldName,
        isDecision,
        isKnm,
        isPm,
      })}
      button={
        <div className={styles.ActionButtonsWrapper}>
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={addEvidence}
            disabled={!isSupervisor || watchOnly || isNeedActSaveOrSign}
          />
          {updateAction && (
            <Button
              className="shared-btn-add"
              text="Обновить"
              type="button"
              variant="link"
              color="primary"
              onClick={updateAction}
              disabled={!isSupervisor || watchOnly || isNeedActSaveOrSign}
            />
          )}
        </div>
      }
    >
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          if (!fieldName || !props.errors) return null;

          const fieldError = getIn(props.errors, fieldName);
          const isFieldTouched = props.touched?.[fieldName];

          if (fieldError === REQUIRED_ERROR && isFieldTouched) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={fieldName}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmViolationEvidence, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((evidenceFieldName, sIndex) => {
                const fieldValue: IKnmErknmViolationEvidence = fields.value?.[sIndex];
                const baseId = `${id}[${fieldValue?.listId}]`;

                const isDisabledByOriginPassportLink =
                  !isNil(fieldValue?.originPassportId) && disableOriginPassportLinkEditable;

                const hasError = checkHasErrorInCollapseBlock(formRef, evidenceFieldName);
                const typeCode = fieldValue?.typeDocConfirmationViolation?.code;
                const isPhoto = isPhotoTypeDocConfirmationViolation(typeCode);
                const isVideo = isVideoTypeDocConfirmationViolation(typeCode);

                return !onlyLinkView ? (
                  <KnmCollapse
                    id={baseId}
                    key={`evidence-row-${sIndex}`}
                    title={`Ссылка на видеозапись или фотоматериалы №${sIndex + 1}`}
                    opened={fieldValue.isNew}
                    onClose={
                      !isDisabledByOriginPassportLink && !watchOnly ? makeRemoveEvidenceHandler(sIndex) : undefined
                    }
                    isNew={fieldValue.isNew}
                    changeOpenedCallback={e =>
                      setKnmCollapseOpened(state => ({
                        ...state,
                        [evidenceFieldName]: e,
                      }))
                    }
                    error={hasError && knmCollapseOpened[evidenceFieldName]}
                  >
                    <>
                      {isDecision && !!eventOptions.length && (
                        <Row>
                          <FieldBlock
                            id={`${baseId}.eventOption`}
                            label={getTitleFieldWithRedStar({
                              label: 'Мероприятие',
                              field: `${evidenceFieldName}.eventOption`,
                              isDecision,
                            })}
                            color="black"
                            placementLabel="left"
                          >
                            <SelectInputField
                              className="shared-row-field"
                              name={`${evidenceFieldName}.eventOption`}
                              options={eventOptions}
                              isClearable={false}
                              placeholder="Выберите мероприятие"
                              disabled={watchOnly || isDisabledByOriginPassportLink}
                              validate={makeRequired('Поле не может быть пустым')}
                              noOptionsMessage="Значение отсутствует"
                              onChange={() => handleEventChange(evidenceFieldName)}
                            />
                          </FieldBlock>
                        </Row>
                      )}
                      {isDecision && (
                        <Row>
                          <FieldBlock
                            id={`${baseId}.typeDocConfirmationViolation`}
                            label={getTitleFieldWithRedStar({
                              label: 'Тип материала',
                              field: `${evidenceFieldName}.typeDocConfirmationViolation`,
                              isDecision,
                            })}
                            color="black"
                            placementLabel="left"
                          >
                            <SelectInputFieldAutoOptions
                              className="shared-row-field"
                              id={`${evidenceFieldName}.typeDocConfirmationViolation`}
                              name={`${evidenceFieldName}.typeDocConfirmationViolation`}
                              optionName={CommonCatalogsNamesEnum.TYPE_DOC_CONFIRMATION_VIOLATION}
                              isClearable={false}
                              placeholder="Выберите тип материала"
                              disabled={watchOnly || isDisabledByOriginPassportLink}
                              validate={makeRequired('Поле не может быть пустым')}
                              onChange={() => handleTypeChange(evidenceFieldName)}
                            />
                          </FieldBlock>
                        </Row>
                      )}
                      <Row>
                        <FieldBlock
                          id={`${baseId}.description`}
                          label={getTitleFieldWithRedStar({
                            label: 'Фиксация потенциального нарушения',
                            field: `${evidenceFieldName}.description`,
                            isDecision,
                            isKnm,
                            isPm,
                          })}
                          color="black"
                          placementLabel="left"
                        >
                          <TextInputField
                            className={styles.DescriptionField}
                            name={`${evidenceFieldName}.description`}
                            placeholder="Опишите нарушение"
                            disabled={watchOnly || isDisabledByOriginPassportLink}
                          />
                        </FieldBlock>
                      </Row>
                      {(!isDecision || isVideo) && (
                        <Row>
                          <FieldBlock
                            id={`${baseId}.link`}
                            label={getTitleFieldWithRedStar({
                              label: isDecision ? 'Ссылка на видеозапись' : 'Ссылка на видеозапись или фотоматериалы',
                              field: `${evidenceFieldName}.link`,
                              isDecision,
                              isKnm,
                              isPm,
                            })}
                            color="black"
                            placementLabel="left"
                          >
                            <TextInputEditableLinkField
                              className={styles.LinkField}
                              name={`${evidenceFieldName}.link`}
                              placeholder="Вставьте ссылку на материалы"
                              disabled={watchOnly || isDisabledByOriginPassportLink}
                              size="medium"
                            />
                          </FieldBlock>
                        </Row>
                      )}
                      {isDecision && isPhoto && (
                        <ErknmActViolationEventAttachmentsField
                          fieldName={evidenceFieldName}
                          formRef={formRef}
                          material={{
                            guid: fieldValue.guid,
                            isNew: !!fieldValue.isNew,
                            typeDocConfirmationViolation: fieldValue.typeDocConfirmationViolation || null,
                            attachments: fieldValue.attachments || null,
                            links: null,
                          }}
                          typeCode={typeCode || 'Photo'}
                          canEdit={!watchOnly && !isDisabledByOriginPassportLink}
                          isDecision={isDecision}
                          isKnm={isKnm}
                          isPm={isPm}
                          id={baseId}
                        />
                      )}
                      <Row>
                        <FieldBlock
                          id={`${baseId}.date`}
                          label={getTitleFieldWithRedStar({
                            label: 'Дата фиксации',
                            field: `${evidenceFieldName}.date`,
                            isDecision,
                            isKnm,
                            isPm,
                          })}
                          color="black"
                          placementLabel="left"
                        >
                          <DatePickerField
                            className={styles.DateField}
                            id={`${evidenceFieldName}.date`}
                            name={`${evidenceFieldName}.date`}
                            placeholder="Выберите дату"
                            disabled={watchOnly || isDisabledByOriginPassportLink}
                            maxDate={maxDate}
                            minDate={minDate}
                          />
                        </FieldBlock>
                      </Row>
                    </>
                  </KnmCollapse>
                ) : (
                  <div className={styles.OnlyLinkViewWrapper}>
                    <FieldBlock id={`${baseId}.link`} color="black" placementLabel="left">
                      <TextInputEditableLinkField
                        className={styles.LinkField}
                        name={`${evidenceFieldName}.link`}
                        placeholder="Вставьте ссылку на материалы"
                        disabled={watchOnly || isDisabledByOriginPassportLink}
                        size="medium"
                      />
                    </FieldBlock>
                    {!(watchOnly || isDisabledByOriginPassportLink) && (
                      <CloseButton className={styles.DeleteButton} onClick={makeRemoveEvidenceHandler(sIndex)} />
                    )}
                  </div>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmEvidenceStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
});

export const ErknmEvidence = connect(mapStateToProps)(ErknmEvidenceWithoutStore);
